// Twitch badge art by channel for the hosted overlay. A badge name/version
// means different art in different channels (each channel uploads its own
// subscriber and bits badges), so the art is kept per room id, each room's set
// as /api/twitch/badges returns it (global art with the channel's on top), and a
// row looks its badges up in the set of the channel they were earned in. A
// single name/version map for every channel would let a second channel's
// answer replace the first channel's custom subscriber art with Twitch's
// default star.

export interface TwitchBadgeArt {
  url: string;
  title?: string;
}

/** Room id -> `set/version` -> art. */
export type TwitchBadgeRooms = Map<string, Map<string, TwitchBadgeArt>>;

type RowTags = Readonly<Record<string, string | undefined>> | undefined;

/** The channel a row's badges were earned in: the channel a shared-chat row was
 *  sent in, else the row's own. The app's chat reads it the same way. */
export function badgeRoomOf(tags: RowTags): string | undefined {
  return tags?.['source-room-id'] || tags?.['room-id'] || undefined;
}

/** A row sent in another channel of a shared chat session. Its badges are that
 *  channel's, so they are not the chatter's look in this one. */
export function isFromOtherRoom(tags: RowTags): boolean {
  const source = tags?.['source-room-id'];
  return !!source && source !== tags?.['room-id'];
}

/** Every room whose badge set a row needs. */
export function badgeRoomsFor(tags: RowTags): string[] {
  const own = tags?.['room-id'];
  const source = badgeRoomOf(tags);
  return [...new Set([own, source].filter((r): r is string => !!r))];
}

/** Store one room's answer from /api/twitch/badges. */
export function storeRoomBadges(
  rooms: TwitchBadgeRooms,
  roomId: string,
  badges: Record<string, string>,
  titles: Record<string, string> = {},
): void {
  const set = new Map<string, TwitchBadgeArt>();
  for (const [key, url] of Object.entries(badges)) set.set(key, { url, title: titles[key] });
  rooms.set(roomId, set);
}

/** The art for one `set/version` badge on a row. A shared-chat row whose own
 *  channel's set has not loaded yet borrows the row's room set until it does. */
export function lookupTwitchBadge(rooms: TwitchBadgeRooms, tags: RowTags, key: string): TwitchBadgeArt | undefined {
  const room = badgeRoomOf(tags);
  const set = (room ? rooms.get(room) : undefined) ?? (tags?.['room-id'] ? rooms.get(tags['room-id']) : undefined);
  return set?.get(key);
}
